import {
  normalizeParsedImportText,
  normalizeTextForImport,
  splitIntoSentences,
} from './importLogsParsingText';
import {
  CARE_TIME_RANGE_SENTENCE_REGEX,
  extractCareTimeFromText,
} from '../../../shared/careDateTimePatterns';

export const parseDateForStorage = (value) => {
  if (!value) return new Date();
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? new Date() : value;

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  return new Date();
};

export const formatLocalTimeForStorage = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return [String(date.getHours()).padStart(2, '0'), String(date.getMinutes()).padStart(2, '0')].join(':');
};

const hasMeaningfulTime = (value) => {
  const date = parseDateForStorage(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  return date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0 || date.getMilliseconds() !== 0;
};

const inferMeridiemFromContext = (text = '', _hour = 0) => {
  const comparable = normalizeTextForImport(text);
  if (/\b(a\.?m\.?)\b/i.test(text)) return 'am';
  if (/\b(p\.?m\.?)\b/i.test(text)) return 'pm';
  if (/(outside time|outside|afternoon|later|playground)\b/i.test(comparable)) return 'pm';
  if (/(snack|breakfast|morning|centers|reading)\b/i.test(comparable)) return 'am';
  if (/(afternoon|later|rest time|after lunch|lunch|after school|evening|dismissal|end of day|pm)\b/i.test(comparable)) return 'pm';
  if (/(morning|breakfast|arrival|arrived|before school|am)\b/i.test(comparable)) return 'am';
  return null;
};

export const inferApproximateTimeFromContext = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) return null;
  if (/\bafter\s*1\b/i.test(comparable)) return '13:15';
  if (/(afternoon|later)\b/i.test(comparable)) return '14:00';
  if (/(rest time|after lunch|lunch|after school|evening|dismissal|end of day|pm)\b/i.test(comparable)) return '14:00';
  return null;
};

export const inferTimeBlockFromContext = (text = '') => {
  const comparable = normalizeTextForImport(text);
  if (!comparable) {
    return 'general';
  }

  if (/\b(?:breakfast|morning|before school|before leaving home|arrival|arrived|woke up early)\b/i.test(comparable)) {
    return 'morning';
  }

  if (/\b(?:lunch|after lunch|after school|school pickup|nap|afternoon|later in the afternoon|during the afternoon)\b/i.test(comparable)) {
    return 'afternoon';
  }

  if (/\b(?:dinner|evening|bedtime|night|overnight|before bed|after dinner|after medication|fell asleep|asleep)\b/i.test(comparable)) {
    return 'evening';
  }

  const approximateTime = inferApproximateTimeFromContext(comparable);
  if (approximateTime) {
    const hour = Number(approximateTime.split(':')[0]);
    if (Number.isFinite(hour)) {
      if (hour < 12) return 'morning';
      if (hour < 17) return 'afternoon';
      return 'evening';
    }
  }

  return 'general';
};

export const extractTimeFromText = (text = '') => (
  extractCareTimeFromText(text, { inferMeridiemFromContext })
);

export const buildDateWithOptionalTime = (anchorDate, timeValue, contextText = '') => {
  const baseDate = anchorDate instanceof Date && !Number.isNaN(anchorDate.getTime()) ? new Date(anchorDate) : new Date();
  if (!timeValue) {
    const approximateTime = inferApproximateTimeFromContext(contextText);
    if (approximateTime) {
      const [approxHours, approxMinutes] = approximateTime.split(':').map((value) => Number(value));
      baseDate.setHours(approxHours, approxMinutes, 0, 0);
      return baseDate;
    }
    baseDate.setHours(0, 0, 0, 0);
    return baseDate;
  }

  const [hours, minutes] = String(timeValue).split(':').map((value) => Number(value));
  if (Number.isFinite(hours) && Number.isFinite(minutes)) baseDate.setHours(hours, minutes, 0, 0);
  return baseDate;
};

export const resolveImportPreviewTiming = (entry = {}, defaultDate = new Date()) => {
  const noteText = String(entry?.note || entry?.text || entry?.sourceText || entry?.originalText || entry?.content || '').trim();
  const providedDate = entry?.date ? parseDateForStorage(entry.date) : null;
  const extracted = extractTimeFromText(noteText);
  const derivedLocalTime = entry?.localTime
    || extracted.time
    || (providedDate && hasMeaningfulTime(providedDate) ? formatLocalTimeForStorage(providedDate) : null)
    || null;
  const timeSource = derivedLocalTime
    ? (entry?.timeSource && entry.timeSource !== 'missing' ? entry.timeSource : 'explicit')
    : (entry?.timeSource || 'missing');
  const anchorDate = providedDate || parseDateForStorage(defaultDate);
  const resolvedDate = derivedLocalTime
    ? buildDateWithOptionalTime(anchorDate, derivedLocalTime, noteText)
    : (providedDate || null);

  return { date: resolvedDate, timeSource, localTime: derivedLocalTime };
};

const escapeRegExp = (value = '') => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const dropHeaderOnlySentence = (sentence = '') => /^(?:breakfast|lunch|dinner|snack|circle time|rest time|rest)\s+was\.?$/i.test(String(sentence || '').trim());

const normalizeImportSentenceText = (sentence = '') => {
  const trimmed = String(sentence || '').trim();
  if (!trimmed) {
    return '';
  }

  const normalized = normalizeParsedImportText(trimmed)
    .replace(/^[,;:\-\u2013\u2014]\s*/, '')
    .trim();

  if (!normalized) {
    return '';
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export const cleanupImportSentenceTime = (sentence = '', extracted = {}) => {
  const originalSentence = String(sentence || '').trim();
  if (!originalSentence) {
    return '';
  }

  if (!extracted?.time || !extracted?.matchedText) {
    return normalizeImportSentenceText(originalSentence);
  }

  if (CARE_TIME_RANGE_SENTENCE_REGEX.test(originalSentence)) {
    return normalizeImportSentenceText(originalSentence);
  }

  const escapedMatchedText = escapeRegExp(extracted.matchedText);
  const cleanedSentence = originalSentence
    .replace(new RegExp(`^${escapedMatchedText}\\s*,?\\s*`, 'i'), '')
    .replace(new RegExp(`\\b(?:was|is)\\s+${escapedMatchedText}\\b`, 'i'), ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (dropHeaderOnlySentence(cleanedSentence)) {
    return '';
  }

  return normalizeImportSentenceText(cleanedSentence);
};

export const buildChunkImportText = (chunk = {}) => {
  const extracted = extractTimeFromText(chunk.text);
  const sentences = splitIntoSentences(chunk.text);
  const cleanedSentences = (sentences.length ? sentences : [chunk.text])
    .map((sentence) => cleanupImportSentenceTime(sentence, extractTimeFromText(sentence)))
    .filter(Boolean);
  const cleanText = normalizeParsedImportText(cleanedSentences.join(' '));

  return {
    extracted,
    cleanText,
  };
};

export const resolveChunkTime = (chunk = {}) => chunk.time || extractTimeFromText(chunk.text || '').time || null;
